import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { MDText } from 'i18n-react';

import { Button, ChevronBoldRightIcon, CrossIcon } from '@motorway/mw-highway-code';
import { SIZE, VARIANT } from '@motorway/mw-highway-code/enums';
import { OptimizelyContext } from '@optimizely/react-sdk';

import { ConfigContext } from 'Context/config';
import { UserContext } from 'Context/user';
import { VehicleLookupContext } from 'Context/vehicleLookup';

import { setCookie } from 'Utilities';
import { getSnowplowIds } from 'Utilities/analytics';
import { SP_HEADER_EVENTS } from 'Utilities/analytics/analyticsHeader';
import { PHOTOS_PAGE_EVENTS } from 'Utilities/analytics/analyticsPhotosPage';
import { EVENT, FLAG } from 'Utilities/analytics/optimizely/constants';
import { useOptimizelyTrack } from 'Utilities/analytics/optimizely/useOptimizelyTrack';
import { INCODE_POST_SALE_SLUG } from 'Utilities/routing';

import { useBreakpoint } from 'Components/misc/Breakpoints/Breakpoints';

import { getAppBannerLink } from './GetTheAppBanner.helpers';
import LocalTexts from './GetTheAppBanner.texts.json';

import styles from './GetTheAppBanner.scss';

const LocalT = new MDText(LocalTexts);

const TEXT = {
  cta: LocalT.translate('cta'),
  heading: LocalT.translate('heading'),
  subheading: LocalT.translate('subheading'),
};

export const GetTheAppBanner = () => {
  const { pathname } = useLocation();
  const { userState: user } = useContext(UserContext);
  const { vehicleLookupState: vehicle } = useContext(VehicleLookupContext);
  const {
    configState: {
      affiliate,
      appClipUrl: sliplaneUrl,
      experimentDecisions,
      hideGetTheAppBanner,
    },
  } = useContext(ConfigContext);

  const [hideBanner, setHideBanner] = useState(hideGetTheAppBanner);
  const [snowplowId, setSnowplowId] = useState('');

  useEffect(() => {
    const sessionId = getSnowplowIds()?.snowplowSessionId ?? '';
    setSnowplowId(sessionId);
  }, []);

  const buttonLink = getAppBannerLink({
    loginToken: user?.login_token,
    pathname,
    sliplaneUrl,
    xSpId: snowplowId,
  });

  const { OPTIMIZELY_TRACK } = useOptimizelyTrack();
  const { optimizely } = useContext(OptimizelyContext);
  const isGetTheAppBannerOn = experimentDecisions?.[FLAG.MW_0_2_GET_THE_APP_BANNER] === 'on';

  const isExcludedPath = affiliate?.showBranding || pathname.includes(INCODE_POST_SALE_SLUG);

  const breakpoints = useBreakpoint();
  const mobileBreakpoint = breakpoints.maxWidth.breakpointTabletMobile;

  const shouldHideBanner = isExcludedPath || hideBanner || !isGetTheAppBannerOn;

  useEffect(() => {
    optimizely?.onReady().then(() => {
      if (!shouldHideBanner && mobileBreakpoint) {
        optimizely.decide(FLAG.MW_0_2_GET_THE_APP_BANNER);
        SP_HEADER_EVENTS.GET_THE_APP_LOADED();
      }
    });
  }, [shouldHideBanner, optimizely, mobileBreakpoint]);

  if (shouldHideBanner) {
    return null;
  }

  const handleClick = () => {
    if (buttonLink) {
      SP_HEADER_EVENTS.GET_THE_APP_BANNER_CLICKED(buttonLink);
    }
    OPTIMIZELY_TRACK(EVENT.GET_THE_APP_BANNER_CLICKED);

    PHOTOS_PAGE_EVENTS.SLIPLANE_HANDOVER({
      vrm: vehicle?.vrm ?? '',
      xSpId: getSnowplowIds()?.snowplowSessionId ?? '',
    });
  };

  const handleClose = () => {
    SP_HEADER_EVENTS.GET_THE_APP_BANNER_CLOSED();
    OPTIMIZELY_TRACK(EVENT.GET_THE_APP_BANNER_HIDDEN);
    setCookie('hideGetTheAppBanner', 'true', 365);
    setHideBanner(true);
  };

  return (
    <section className={styles.banner}>
      <div className={styles.content}>
        <Button as='button' icon={CrossIcon} label="Close" onClick={handleClose} showLabel={false} size={SIZE.SMALL} variant={VARIANT.TERTIARY}/>
        <div className={styles.chevron}>
          <ChevronBoldRightIcon size='medium'/>
        </div>
        <div className={styles.text}>
          <span className={styles.heading}>{TEXT.heading}</span>
          <span className={styles.subheading}>{TEXT.subheading}</span>
        </div>
      </div>
      <Button as='a' href={buttonLink} label={TEXT.cta} onClick={handleClick} rel='noopener noreferrer' size={SIZE.SMALL} target='_blank'/>
    </section>
  );
};
